import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Folders,
  CheckCircle2,
  FileText,
  TrendingUp,
  Wallet,
  Coins,
  ArrowUpRight,
  Receipt,
  ListTodo,
  HandCoins,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, formatPLN } from "../lib/api";

const MONTHS = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];
const MONTHS_SHORT = ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"];

const actions = [
  {
    to: "/zlecenia/nowe",
    label: "Nowe zlecenie",
    desc: "Załóż nowe zlecenie i wprowadzaj koszty na bieżąco.",
    icon: Plus,
    color: "var(--primary)",
    testid: "action-new-order",
  },
  {
    to: "/zlecenia",
    label: "Przeglądaj zlecenia",
    desc: "Aktywne zlecenia w toku.",
    icon: Folders,
    color: "#3B5249",
    testid: "action-orders",
  },
  {
    to: "/zakonczone",
    label: "Zakończone",
    desc: "Historia zleceń i zarobki.",
    icon: CheckCircle2,
    color: "#1A1A1A",
    testid: "action-completed",
  },
  {
    to: "/wyceny",
    label: "Wyceny",
    desc: "Szybkie wyceny dla klientów.",
    icon: FileText,
    color: "#A43725",
    testid: "action-quotes",
  },
  {
    to: "/koszty",
    label: "Koszty działalności",
    desc: "Stałe i zmienne koszty firmy.",
    icon: Receipt,
    color: "#5C5C5C",
    testid: "action-costs",
  },
  {
    to: "/zadania",
    label: "Lista zadań",
    desc: "Twoja prywatna lista to-do.",
    icon: ListTodo,
    color: "#3B5249",
    testid: "action-tasks",
  },
];

export default function Dashboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/stats", { params: { year, month } })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, [year, month]);

  const years = stats?.available_years?.length
    ? stats.available_years
    : [now.getFullYear()];

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
            Pulpit
          </div>
          <h1 className="heading text-4xl sm:text-5xl font-semibold tracking-tight max-w-2xl">
            Panel zarządzania <span className="text-[var(--primary)]">stolarnią</span>
          </h1>
          <p className="text-base text-[var(--text-muted)] mt-3 max-w-xl">
            Wszystko, czego potrzebujesz do prowadzenia zleceń meblarskich w jednym miejscu.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="stats-grid">
        <StatTileWithSelect
          icon={Wallet}
          label="Zarobek miesiąc"
          value={formatPLN(stats?.monthly_profit ?? 0)}
          accent="var(--primary)"
          testid="stat-monthly"
        >
          <div className="flex gap-2 mt-3" data-testid="month-selectors">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-8 text-xs" data-testid="select-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)} data-testid={`month-opt-${i + 1}`}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-8 text-xs w-[88px]" data-testid="select-year-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </StatTileWithSelect>

        <StatTileWithSelect
          icon={TrendingUp}
          label="Zarobek rok"
          value={formatPLN(stats?.yearly_profit ?? 0)}
          accent="#3B5249"
          testid="stat-yearly"
        >
          <div className="flex gap-2 mt-3" data-testid="year-selectors">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-8 text-xs" data-testid="select-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </StatTileWithSelect>

        <div className="space-y-4" data-testid="stat-third-col">
          <div
            className="surface p-4"
            data-testid="stat-pending-payments"
            title="Suma kwot 'Pozostało do zapłaty' z aktywnych zleceń"
          >
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-xs">
              <span
                className="w-6 h-6 rounded flex items-center justify-center text-white"
                style={{ background: "#A43725" }}
              >
                <HandCoins size={12} />
              </span>
              Pozostało do zapłaty
            </div>
            <div className="heading text-xl font-bold mt-2 tabular-nums">
              {formatPLN(stats?.pending_payments ?? 0)}
            </div>
          </div>

          <StatTileWithSelect
            icon={Coins}
            label="Łączny zarobek (zlecenia)"
            value={formatPLN(stats?.total_profit ?? 0)}
            accent="#1A1A1A"
            testid="stat-total"
          >
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <SmallStat label="W toku" value={stats?.active_orders ?? 0} testid="mini-active" />
              <SmallStat
                label="Zakończone"
                value={stats?.completed_orders ?? 0}
                testid="mini-completed"
              />
            </div>
          </StatTileWithSelect>
        </div>
      </section>

      <section>
        <h2 className="heading text-lg font-semibold mb-4">Szybkie akcje</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.to}
                to={a.to}
                data-testid={a.testid}
                className="tab-action surface p-6 flex items-start gap-4 group"
              >
                <span
                  className="w-12 h-12 rounded-md flex items-center justify-center text-white shrink-0"
                  style={{ background: a.color }}
                >
                  <Icon size={20} />
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="heading text-lg font-semibold">{a.label}</div>
                    <ArrowUpRight
                      className="text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors"
                      size={18}
                    />
                  </div>
                  <p className="text-sm text-[var(--text-muted)] mt-1">{a.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="surface p-6" data-testid="yearly-chart-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="heading text-lg font-semibold">
              Zarobek wg miesiąca · {year}
            </h2>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Po odjęciu kosztów stałych (od miesiąca dodania) i zmiennych (z danego miesiąca)
            </p>
          </div>
        </div>
        <YearlyBarChart
          data={stats?.monthly_series || []}
          activeMonth={month}
          onSelectMonth={setMonth}
        />
        <Legend stats={stats} />
      </section>
    </div>
  );
}

function StatTileWithSelect({ icon: Icon, label, value, accent, testid, children }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
        <span
          className="w-7 h-7 rounded flex items-center justify-center text-white"
          style={{ background: accent }}
        >
          <Icon size={14} />
        </span>
        {label}
      </div>
      <div className="heading text-3xl font-bold mt-3 tabular-nums">{value}</div>
      {children}
    </div>
  );
}

function SmallStat({ label, value, testid }) {
  return (
    <div className="surface-2 px-3 py-2 rounded-md" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </div>
      <div className="heading font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function YearlyBarChart({ data, activeMonth, onSelectMonth }) {
  const chartData = (data && data.length === 12)
    ? data.map((d) => ({
        ...d,
        label: MONTHS_SHORT[d.month_num - 1],
      }))
    : Array.from({ length: 12 }, (_, i) => ({
        month_num: i + 1,
        label: MONTHS_SHORT[i],
        profit: 0,
        orders_profit: 0,
        variable_costs: 0,
        fixed_costs: 0,
      }));

  return (
    <div className="h-[280px] w-full" data-testid="yearly-bar-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: "#5C5C5C" }}
            axisLine={{ stroke: "#E5E0D8" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#5C5C5C" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(140,63,34,0.05)" }} />
          <Bar
            dataKey="profit"
            radius={[6, 6, 0, 0]}
            onClick={(d) => onSelectMonth?.(d.month_num)}
            cursor="pointer"
          >
            {chartData.map((d, idx) => {
              const isActive = d.month_num === activeMonth;
              const positive = d.profit >= 0;
              const color = positive
                ? isActive ? "var(--primary)" : "#C97D5A"
                : isActive ? "var(--danger)" : "#D87862";
              return <Cell key={idx} fill={color} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="surface p-3 text-xs space-y-1" data-testid="chart-tooltip">
      <div className="heading font-semibold text-sm mb-1">
        {MONTHS[d.month_num - 1]}
      </div>
      <Row k="Zlecenia" v={formatPLN(d.orders_profit)} />
      <Row k="Koszty stałe" v={`-${formatPLN(d.fixed_costs)}`} />
      <Row k="Koszty zmienne" v={`-${formatPLN(d.variable_costs)}`} />
      <div className="border-t border-[var(--border)] pt-1 mt-1">
        <Row
          k="Razem"
          v={formatPLN(d.profit)}
          accent={d.profit >= 0 ? "var(--success)" : "var(--danger)"}
          bold
        />
      </div>
    </div>
  );
}

function Row({ k, v, accent, bold }) {
  return (
    <div className="flex justify-between gap-6">
      <span className="text-[var(--text-muted)]">{k}</span>
      <span
        className="tabular-nums"
        style={{ color: accent, fontWeight: bold ? 600 : 500 }}
      >
        {v}
      </span>
    </div>
  );
}

function Legend({ stats }) {
  if (!stats) return null;
  return (
    <div className="flex flex-wrap gap-4 mt-4 text-xs text-[var(--text-muted)]" data-testid="chart-legend">
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm" style={{ background: "var(--primary)" }} />
        Aktywny miesiąc
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm" style={{ background: "#C97D5A" }} />
        Zysk
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm" style={{ background: "#D87862" }} />
        Strata
      </span>
      <span className="ml-auto">
        Koszty stałe ogółem: <strong className="text-[var(--text)]">{formatPLN(stats.business_costs_fixed)}</strong>
      </span>
    </div>
  );
}
